import { useFormatBreederContactInfo } from '@application/api';
import {
  joinWithAnd,
  translatedFieldWithFallback,
  useModal,
  useRouter,
  useSlideshow,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import { BreederOverviewModal } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { useGetBreeder } from './api/useGetBreeder';

const init: ReturnType<typeof useBreederOverviewModalSingleton> = {
  breederId: undefined,
  modalHook: {
    show: () => undefined,
    showFromClick: () => undefined,
    close: () => undefined,
    modal: {
      onCloseClick: undefined,
      open: false,
      position: [0, 0],
    },
  },
};

let globalBreederId: Dispatch<SetStateAction<number | undefined>>;

export const useBreederOverviewModalSingleton = () => {
  const modalHook = useModal({});
  const [breederId, setBreederId] = useState<number>();
  globalBreederId = setBreederId;

  return {
    modalHook,
    breederId,
  };
};

export const setBreederForOverviewId = (breederId: number) => globalBreederId(breederId);

export const useBreederOverviewModal = singletonHook(init, useBreederOverviewModalSingleton);

export const BreederOverviewModalController = () => {
  const { language } = useRouter();
  const { translate } = useTranslation();
  const { modalHook, breederId } = useBreederOverviewModal();
  const { breeder, breederFilteredBreeds, breederLoading } = useGetBreeder(breederId);

  const breederImages = useMemo(() => {
    return (breeder?.imgs ?? []).filter((img) => !['cover', 'default'].includes(img.type));
  }, [breeder?.imgs]);
  const slideshow = useSlideshow(
    breederImages.map((image) => ({ src: image.large ?? '', thumbnail: image.small ?? '' })),
  );

  const { contactInfo } = useFormatBreederContactInfo(breeder);

  const breedNames = useMemo(() => {
    return (breederFilteredBreeds ?? [])
      .map(
        ({ dogBreed }) =>
          (dogBreed?.translations ?? []).find((x) => x.language === language)?.name ?? undefined,
      )
      .filter((x) => !!x) as string[];
  }, [breederFilteredBreeds, language]);

  const titleMultipleNames = useMemo(() => {
    if (!breedNames?.length || !breeder?.account.address?.country)
      return translate('breederDetails.header.titleEmpty');
    const names = joinWithAnd(translate, breedNames ?? []);
    return translate('breederDetails.header.title')
      .replaceAll('$breeds', names ?? '')
      .replaceAll(
        '$country',
        translate(`enum.country.${breeder?.account.address?.country ?? CountryCode.BE}`),
      );
  }, [breedNames, breeder?.account.address?.country, translate]);

  return (
    <BreederOverviewModal
      modal={modalHook.modal}
      loading={breederLoading}
      title={titleMultipleNames}
      content={{
        description: translatedFieldWithFallback(language, breeder?.translations, 'description'),
        puppiesLivingPlace: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'puppiesLivingPlace',
        ),
        livingPlace: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'dogsLivingPlace',
        ),
        parentSelection: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'breederDogsSelection',
        ),
        puppiesWork: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'puppiesTraining',
        ),
      }}
      breedNames={breedNames}
      contactInfo={contactInfo}
      {...(slideshow.items.length ? { slideshow } : {})}
    />
  );
};
